import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ChevronRight, Check, Shield, User, Eye } from 'lucide-react';
import { useOrganization } from '../contexts/OrganizationContext';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../supabaseClient';

interface OrgWithRole {
  org: any;
  role: string;
}

const OrganizationSelector: React.FC = () => {
  const { userOrganizations, switchOrganization, loading, currentOrganization, showSelectorOnLoad } = useOrganization();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(currentOrganization?.id || null);
  const [orgsWithRoles, setOrgsWithRoles] = useState<OrgWithRole[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(true);

  // Fetch roles for each organization
  useEffect(() => {
    const fetchRoles = async () => {
      if (!user || userOrganizations.length === 0) {
        setLoadingRoles(false);
        return;
      }

      try {
        const { data: memberships, error } = await supabase
          .from('organization_members')
          .select('organization_id, role')
          .eq('user_id', user.id)
          .in('organization_id', userOrganizations.map(org => org.id));

        if (error) {
          console.error('Error fetching roles:', error);
          setLoadingRoles(false);
          return;
        }

        const orgsWithRolesData = userOrganizations.map(org => {
          const membership = memberships?.find(m => m.organization_id === org.id);
          return {
            org,
            role: membership?.role || 'member'
          };
        });

        setOrgsWithRoles(orgsWithRolesData);
      } catch (error) {
        console.error('Error in fetchRoles:', error);
      } finally {
        setLoadingRoles(false);
      }
    };

    fetchRoles();
  }, [user, userOrganizations]);

  // Don't show if loading, no orgs, only one org, or (org already selected AND not forcing selector)
  if (loading || loadingRoles || userOrganizations.length === 0 || userOrganizations.length === 1 || (currentOrganization && !showSelectorOnLoad)) {
    return null;
  }

  const handleSelectOrganization = async (orgId: string) => {
    setSelectedOrgId(orgId);
    
    // First, save to localStorage immediately
    localStorage.setItem('currentOrganizationId', orgId);
    
    // Clear the justLoggedIn flag BEFORE switching
    sessionStorage.removeItem('justLoggedIn');
    
    // Then switch organization
    await switchOrganization(orgId);
    
    // Navigate to dashboard without page reload
    navigate('/');
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner':
      case 'admin':
        return <Shield className="w-4 h-4" />;
      case 'technician':
      case 'supervisor':
        return <User className="w-4 h-4" />;
      case 'viewer':
        return <Eye className="w-4 h-4" />;
      default:
        return <User className="w-4 h-4" />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300';
      case 'admin':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
      case 'supervisor':
        return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300';
      case 'technician':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
      case 'viewer':
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 px-4 py-12">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-4">
            <Building2 className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Welcome back{user?.email ? `, ${user.email.split('@')[0]}` : ''}!
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            You're a member of multiple organizations. Please select which organization you'd like to access.
          </p>
        </div>

        {/* Organizations List */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Select an Organization
            </h2>
            
            <div className="space-y-3">
              {orgsWithRoles.map(({ org, role }) => {
                const isSelected = selectedOrgId === org.id;
                
                return (
                  <button
                    key={org.id}
                    onClick={() => handleSelectOrganization(org.id)}
                    disabled={isSelected}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-200
                      ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-500'
                          : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }
                      ${isSelected ? 'cursor-default' : 'cursor-pointer'}
                    `}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1 min-w-0">
                        <div className="flex-shrink-0">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                            isSelected 
                              ? 'bg-blue-500' 
                              : 'bg-gray-100 dark:bg-gray-700'
                          }`}>
                            <Building2 className={`w-6 h-6 ${
                              isSelected 
                                ? 'text-white' 
                                : 'text-gray-600 dark:text-gray-300'
                            }`} />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 dark:text-white text-lg truncate">
                            {org.name}
                          </h3>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded-md text-xs font-medium ${getRoleColor(role)}`}>
                              {getRoleIcon(role)}
                              <span>{role.charAt(0).toUpperCase() + role.slice(1)}</span>
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 flex-shrink-0">
                        {isSelected && (
                          <div className="flex items-center space-x-2 text-blue-600 dark:text-blue-400">
                            <Check className="w-5 h-5" />
                            <span className="text-sm font-medium">Selected</span>
                          </div>
                        )}
                        {!isSelected && (
                          <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              You can switch organizations anytime from the header menu
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationSelector;

